import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, BarChart3, Users, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";

const HeroSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const handleGetStarted = () => {
    if (user) {
      navigate("/home");
    } else {
      navigate("/login");
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 30 }, // Increased y from 20 to 30
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section aria-labelledby="hero-heading" className="relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.15),transparent_45%)]"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 pt-16 pb-20 sm:px-6 lg:px-8 lg:pt-20 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left column - Text content */}
          <motion.div 
            variants={container}
            initial="hidden"
            animate="show"
            className="flex flex-col space-y-6 md:space-y-8"
          >
            <motion.h1 
              id="hero-heading"
              variants={item}
              className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-center lg:text-left"
            >
              <span className="text-primary">Chito</span> - Simplify Your Chit Fund Management
            </motion.h1>
            
            <motion.p 
              variants={item}
              className="mt-4 text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 text-center lg:text-left"
            >
              An intuitive platform designed specifically for chit fund managers to organize, track, and grow their funds with ease.
            </motion.p>

            <motion.div 
              variants={item}
              className="flex flex-wrap gap-4 pt-2 justify-center lg:justify-start"
            >
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="group text-base h-12 px-6 shadow-md hover:shadow-lg transition-all w-full sm:w-auto"
              >
                Get Started
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="text-base h-12 px-6 w-full sm:w-auto"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </motion.div>

            <motion.div 
              variants={item}
              className="pt-6 flex flex-col sm:flex-row gap-4 sm:gap-8 justify-center lg:justify-start"
            >
              {[
                { icon: <Users className="h-5 w-5 text-primary" />, text: "Manage multiple funds" },
                { icon: <Clock className="h-5 w-5 text-primary" />, text: "Automated reminders" },
                { icon: <BarChart3 className="h-5 w-5 text-primary" />, text: "Detailed analytics" }
              ].map((feature, index) => (
                <div key={index} className="flex items-center justify-center lg:justify-start">
                  {feature.icon}
                  <span className="ml-2 text-sm text-muted-foreground">{feature.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
          
          {/* Right column - Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative mx-auto lg:mx-0 w-full max-w-xl lg:max-w-none"
          >
            <div className="relative bg-gradient-to-br from-muted via-card to-background p-2 rounded-xl border shadow-lg overflow-hidden">
              <div className="aspect-[16/9] bg-card rounded-lg overflow-hidden">
                <div className="p-4 sm:p-6 flex flex-col h-full">
                  <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <div className="flex items-center">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary flex items-center justify-center mr-2 sm:mr-3">
                        <span className="text-primary-foreground font-bold">C</span>
                      </div>
                      <span className="text-lg sm:text-xl font-semibold">Chito Dashboard</span>
                    </div>
                    <div className="flex space-x-1 sm:space-x-2">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-400"></div>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-400"></div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6">
                    {[
                      { title: "Active Chit Funds", value: "12", color: "bg-primary/10" },
                      { title: "Total Members", value: "156", color: "bg-green-500/10" },
                      { title: "This Month", value: "₹48,500", color: "bg-blue-500/10" },
                      { title: "Total Volume", value: "₹4.2L", color: "bg-purple-500/10" }
                    ].map((stat, i) => (
                      <div key={i} className={`${stat.color} p-3 sm:p-4 rounded-lg`}>
                        <div className="text-xs sm:text-sm text-muted-foreground">{stat.title}</div>
                        <div className="text-lg sm:text-2xl font-bold mt-1">{stat.value}</div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex-1 bg-muted/50 rounded-lg p-2 sm:p-3">
                    <div className="mb-1 sm:mb-2 text-xs sm:text-sm font-medium">Recent Activity</div>
                    {[
                      "Rahul Sharma lifted ₹50,000",
                      "3 new members joined Silver Pool",
                      "Payment reminder sent to 12 members"
                    ].map((activity, i) => (
                      <div key={i} className="flex items-center py-1 sm:py-2 border-b border-border/50 last:border-0">
                        <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1 sm:mr-2 flex-shrink-0" />
                        <span className="text-xs sm:text-sm">{activity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -z-10 h-40 w-40 bg-primary/20 rounded-full blur-3xl top-1/2 -left-20 opacity-70"></div>
            <div className="absolute -z-10 h-40 w-40 bg-accent/20 rounded-full blur-3xl bottom-10 right-10 opacity-70"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
